import { createSlice } from '@reduxjs/toolkit';

const loadCartFromStorage = () => {
  try {
    const savedCart = localStorage.getItem('smart_cart_items');
    return savedCart ? JSON.parse(savedCart) : [];
  } catch (error) {
    console.error('Error loading cart from storage', error);
    return [];
  }
};

const loadLanguageFromStorage = () => {
  try {
    const savedLang = localStorage.getItem('smart_store_lang');
    return savedLang ? JSON.parse(savedLang) : { name: 'English', code: 'en', flagUrl: 'https://purecatamphetamine.github.io/country-flag-icons/3x2/US.svg' };
  } catch (error) {
    console.error('Error loading language from storage', error);
    return { name: 'English', code: 'en', flagUrl: 'https://purecatamphetamine.github.io/country-flag-icons/3x2/US.svg' };
  }
};

const initialState = {
  items: loadCartFromStorage(),
  selectedCategory: 'All',
  selectedSubcategory: 'All',
  selectedBrand: 'All',
  language: loadLanguageFromStorage(),
  searchQuery: '',
};

const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    addToCart: (state, action) => {
      const { id, name, price, originalPrice, b2bPrice, image, quantity = 1, category, shippingCharges, deliveryTime } = action.payload;
      const existingItem = state.items.find(item => item.id === id);

      if (existingItem) {
        existingItem.quantity += quantity;
      } else {
        state.items.push({
          id,
          name,
          price: Number(price),
          b2bPrice: b2bPrice ? Number(b2bPrice) : undefined,
          originalPrice: originalPrice ? Number(originalPrice) : undefined,
          image,
          quantity: Number(quantity),
          category,
          shippingCharges: shippingCharges ? Number(shippingCharges) : 0,
          deliveryTime
        });
      }
      localStorage.setItem('smart_cart_items', JSON.stringify(state.items));
    },
    removeFromCart: (state, action) => {
      const id = action.payload;
      state.items = state.items.filter(item => item.id !== id);
      localStorage.setItem('smart_cart_items', JSON.stringify(state.items));
    },
    updateQuantity: (state, action) => {
      const { id, quantity } = action.payload;
      const existingItem = state.items.find(item => item.id === id);
      if (existingItem && quantity >= 1) {
        existingItem.quantity = Number(quantity);
      }
      localStorage.setItem('smart_cart_items', JSON.stringify(state.items));
    },
    clearCart: (state) => {
      state.items = [];
      localStorage.removeItem('smart_cart_items');
    },
    setSelectedCategory: (state, action) => {
      state.selectedCategory = action.payload;
      // Changing category clears subcategory unless set again right after
      state.selectedSubcategory = 'All';
    },
    setSelectedSubcategory: (state, action) => {
      state.selectedSubcategory = action.payload || 'All';
    },
    setSelectedBrand: (state, action) => {
      state.selectedBrand = action.payload;
    },
    setLanguage: (state, action) => {
      state.language = action.payload;
      localStorage.setItem('smart_store_lang', JSON.stringify(action.payload));
    },
    setSearchQuery: (state, action) => {
      state.searchQuery = action.payload;
    }
  }
});

export const {
  addToCart,
  removeFromCart,
  updateQuantity,
  clearCart,
  setSelectedCategory,
  setSelectedSubcategory,
  setSelectedBrand,
  setLanguage,
  setSearchQuery,
} = cartSlice.actions;

// Helper to determine B2B state from localStorage
const getB2bStatus = () => {
  try {
    const session = localStorage.getItem('user_session');
    const user = session ? JSON.parse(session) : null;
    return !!(user && user.isB2B);
  } catch (error) {
    return false;
  }
};

// Selectors
export const selectCartItems = (state) => {
  const isB2b = getB2bStatus();
  return state.cart.items.map(item => ({
    ...item,
    price: (isB2b && item.b2bPrice) ? item.b2bPrice : item.price
  }));
};
export const selectCartCount = (state) => state.cart.items.reduce((total, item) => total + item.quantity, 0);
export const selectCartTotal = (state) => {
  const isB2b = getB2bStatus();
  return state.cart.items.reduce((total, item) => {
    const itemPrice = (isB2b && item.b2bPrice) ? item.b2bPrice : item.price;
    return total + (itemPrice * item.quantity);
  }, 0);
};
export const selectSelectedCategory = (state) => state.cart.selectedCategory;
export const selectSelectedSubcategory = (state) => state.cart.selectedSubcategory;
export const selectSelectedBrand = (state) => state.cart.selectedBrand;
export const selectLanguage = (state) => state.cart.language;
export const selectSearchQuery = (state) => state.cart.searchQuery;

export default cartSlice.reducer;
